import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { db } from '../../lib/firebase';
import ClassCodeModal from '../ClassCodeModal/ClassCodeModal';
import Styles from './UpgradeModal.module.css';

const PERKS = [
  'All 6 helpers',
  'Unlimited scale practice (free is 5 runs a day)',
  'Custom ranges & key signatures',
  '24 hours of offline earnings',
];

const UpgradeModal = ({ onClose, reason }) => {
  const navigate = useNavigate();
  const [pendingPlan, setPendingPlan] = useState(null);
  const [showClassCode, setShowClassCode] = useState(false);

  // The Escape handler below lives on window, so it reads this ref instead of
  // state. Otherwise one Escape press would close a sub-modal AND this one.
  const subModalOpenRef = useRef(false);
  subModalOpenRef.current = showClassCode;

  // Escape to close + lock background scroll
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !pendingPlan && !subModalOpenRef.current) onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, pendingPlan]);

  const handleSubscribe = async (priceId) => {
    if (!getAuth().currentUser) {
      onClose();
      navigate('/signup');
      return;
    }
    setPendingPlan(priceId);
    try {
      const user = getAuth().currentUser;
      let username = '';

      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) username = userDoc.data().username;
      }

      const res = await fetch(
        'https://brassflows-backend.onrender.com/create-checkout-session',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            priceId,
            username,
            uid: user?.uid || '',
            email: user?.email || '',
          }),
        },
      );
      const json = await res.json();
      if (!res.ok) throw json;
      window.location = json.url;
    } catch (e) {
      console.error(e?.error || e);
      toast.error('Something went wrong. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setPendingPlan(null);
    }
  };

  const busy = Boolean(pendingPlan);

  const renderButton = (priceId, label, variant) => (
    <button
      type="button"
      className={`${Styles.cta} ${variant === 'solid' ? Styles.ctaSolid : Styles.ctaOutline}`}
      disabled={busy}
      onClick={() => handleSubscribe(priceId)}
    >
      {pendingPlan === priceId ? <span className={Styles.loader} /> : label}
    </button>
  );

  // The class-code modal replaces the plan picker; closing it drops back here.
  if (showClassCode) {
    // onClose = back to the plans, onDone = close everything (used after joining)
    return <ClassCodeModal onClose={() => setShowClassCode(false)} onDone={onClose} />;
  }

  return createPortal(
    <div className={Styles.overlay} onClick={() => !busy && onClose()}>
      <div
        className={Styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={Styles.close}
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
        >
          ×
        </button>

        <p className={Styles.eyebrow}>Brassflows Premium</p>
        <h2 id="upgrade-title" className={Styles.title}>
          Unlock the full Brassflows experience
        </h2>

        <ul className={Styles.perks}>
          {PERKS.map((p) => (
            <li key={p} className={Styles.perk}>
              <span className={Styles.check}>✓</span>
              {p}
            </li>
          ))}
        </ul>

        <div className={Styles.plans}>
          {/* Monthly */}
          <div className={Styles.plan}>
            <h3 className={Styles.planName}>Monthly</h3>
            <div className={Styles.priceRow}>
              <span className={Styles.price}>$0.99</span>
            </div>
            <p className={Styles.planNote}>first month</p>
            <p className={Styles.planFine}>
              Then $3.99/mo <span className={Styles.pill}>75% off</span>
            </p>
            {renderButton('price_monthly', 'Subscribe', 'outline')}
          </div>

          {/* Yearly (featured) */}
          <div className={`${Styles.plan} ${Styles.featured}`}>
            <span className={Styles.badge}>Best value · Save 69%</span>
            <h3 className={Styles.planName}>Yearly</h3>
            <div className={Styles.priceRow}>
              <span className={Styles.price}>$14.99</span>
            </div>
            <p className={Styles.planNote}>per year</p>
            <p className={Styles.planFine}>
              <strong>Only $1.24/month</strong>
            </p>
            {renderButton('price_yearly', 'Subscribe', 'solid')}
          </div>

          {/* Class code */}
          <div className={Styles.plan}>
            <h3 className={Styles.planName}>Have a class code?</h3>
            <div className={Styles.priceRow}>
              <span className={Styles.codeMark} aria-hidden="true">
                _ _ _ _
              </span>
            </div>
            <p className={Styles.planNote}>from your teacher</p>
            <p className={Styles.planFine}>Join your class in seconds</p>
            <button
              type="button"
              className={`${Styles.cta} ${Styles.ctaOutline}`}
              disabled={busy}
              onClick={() => setShowClassCode(true)}
            >
              Enter code
            </button>
          </div>
        </div>

        <p className={Styles.footer}>
          Cancel anytime · Secure checkout via Stripe · Questions?{' '}
          <strong>support@brassflows.com</strong>
        </p>
      </div>
    </div>,
    document.body,
  );
};

export default UpgradeModal;